import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  Brain, Search, Filter, BarChart, Star, MessageSquare, Download, ArrowRight, Cloud, Server, Shield, Database,
  Network, Monitor, Bell, LogOut, Target, Clock, Lock
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import ChatBot from '../components/ChatBot';
import DashboardOverview from '../components/DashboardOverview';

const DashboardPage = () => {
  const [showChatbot, setShowChatbot] = useState(false);
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  
  const userName = user?.user_metadata?.name || 'Usuario';
  const userRole = user?.user_metadata?.role || 'product-manager';

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  // Azure Administrator modules
  const azureModules = [
    {
      id: 'az-900',
      title: 'Microsoft Azure Fundamentals',
      description: 'Learn the foundational concepts of cloud computing and get started with Azure cloud services',
      progress: 0,
      duration: '1 day',
      status: 'available',
      icon: <Cloud className="h-5 w-5 text-accent" />
    },
    {
      id: 'az-104',
      title: 'Microsoft Azure Administrator',
      description: 'Learn to implement, monitor, and maintain Microsoft Azure solutions',
      progress: 0,
      duration: '4 days',
      status: 'available',
      icon: <Shield className="h-5 w-5 text-accent" />
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-background border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <Brain className="h-8 w-8 text-accent" />
                <span className="ml-2 text-xl font-bold text-foreground">
                  Training Portal
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 border border-border rounded-lg bg-secondary/30 text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                />
                <Search className="absolute left-3 top-2.5 h-5 w-5 text-foreground/50" />
              </div>
              <button className="text-foreground/70 hover:text-foreground relative">
                <Bell className="h-5 w-5" />
                <span className="absolute top-0 right-0 block h-2 w-2 rounded-full bg-red-500"></span>
              </button>
              <div className="border-l border-border h-6"></div>
              <div className="flex items-center">
                <div className="h-8 w-8 rounded-full bg-accent/10 flex items-center justify-center text-accent font-medium">
                  {userName[0]}
                </div>
                <button 
                  onClick={handleSignOut}
                  className="ml-4 text-foreground/70 hover:text-foreground"
                >
                  <LogOut className="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <DashboardOverview
          userName={userName}
          userRole={userRole}
          progress={65}
          xp={350}
          onChatWithMentor={() => setShowChatbot(true)}
        />

        {/* Training Modules */}
        <div className="mt-8 dark-card p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-xl font-bold text-foreground">Learning Modules</h2>
              <p className="text-foreground/70 mt-1">Continue your learning journey with these modules</p>
            </div>
          </div>

          <div className="space-y-6">
            {azureModules.map((module) => (
              <div
                key={module.id}
                className="border rounded-lg p-6 hover:border-accent/50 transition-all duration-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="h-10 w-10 rounded-full bg-accent/10 flex items-center justify-center">
                      {module.icon}
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-medium text-foreground">{module.title}</h3>
                      <p className="text-foreground/70">{module.description}</p>
                    </div>
                  </div>
                  {module.status === 'locked' ? (
                    <div className="flex items-center text-foreground/50">
                      <Lock className="h-5 w-5 mr-2" />
                      <span>Locked</span>
                    </div>
                  ) : (
                    <Link 
                      to={module.id === 'az-900' ? '/training/az-900' : `/training/${module.id}`}
                      className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90"
                    >
                      {module.progress > 0 ? 'Continue' : 'Start Course'}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  )}
                </div>

                {module.status !== 'locked' && (
                  <div className="mt-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-foreground/70">Progress</span>
                      <span className="text-sm font-medium text-foreground">{module.progress}%</span>
                    </div>
                    <div className="h-2 bg-secondary rounded-full">
                      <div
                        className="h-2 bg-accent rounded-full transition-all duration-300"
                        style={{ width: `${module.progress}%` }}
                      ></div>
                    </div>
                    <div className="mt-2 flex items-center text-sm text-foreground/70">
                      <Clock className="h-4 w-4 mr-1" />
                      <span>Estimated duration: {module.duration}</span>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {showChatbot && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="w-full max-w-2xl">
            <ChatBot 
              role={userRole} 
              onClose={() => setShowChatbot(false)} 
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;